import React from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Stack,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { breadcrumbSchema, faqPageSchema } from "../seo/structuredData";
import { PRICING_SENTENCE } from "../seo/siteMeta";

import Seo from "./Seo";

/**
 * Phrased as the questions a prospect actually types into a search box or asks an
 * assistant, and answered in complete self-contained sentences: an answer engine
 * quotes one entry without the surrounding page for context.
 *
 * This array also feeds the FAQPage JSON-LD below, so the structured data can never
 * drift from what is rendered.
 */
const faqs = [
  {
    question: "What is BadgeReg?",
    answer:
      "BadgeReg is registration and fundraising software for Scouting merit badge colleges. A troop or crew uses it to publish its class schedule, let parents register their Scouts online, collect payments through PayPal, and track registrations, income, and counselor assignments from an organizer dashboard.",
  },
  {
    question: "How much does BadgeReg cost?",
    answer: PRICING_SENTENCE,
  },
  {
    question: "How do I get started?",
    answer:
      "Contact us using the contact form on this website and we will get back to you as soon as possible. We set up your event, issue your event's web address, and help you load your class schedule before registration opens.",
  },
  {
    question: "Do we need our own PayPal account?",
    answer:
      "No. Payments are collected through BadgeReg's PayPal integration, and the proceeds are dispersed to the event organizer after the fundraiser is complete. PayPal's processing fee is included in the amount collected at checkout.",
  },
  {
    question: "What payment methods can parents use?",
    answer:
      "Parents pay online through PayPal at checkout, which accepts PayPal balances and major credit and debit cards. Organizers can also register a Scout on a pro-bono basis at no charge when a family needs it.",
  },
  {
    question: "Can a parent register more than one Scout?",
    answer:
      "Yes. A parent creates one account, adds each of their Scouts to it, and registers each Scout for their own set of classes. Class choices, schedules, and payments are tracked per Scout under the single parent login.",
  },
  {
    question: "Can Scouts change their classes after registering?",
    answer:
      "Yes. Participants can change their class selections after registration, and BadgeReg automatically charges or refunds the difference through PayPal if the change affects the price.",
  },
  {
    question: "Why does this service cost money?",
    answer:
      "BadgeReg charges a small fee to cover the cost of maintaining the platform and providing support to event organizers. This fee is included in the registration cost paid by each Scout. The $250 setup fee is charged to the event organizer in order to hold accountability.",
  },
  {
    question: "What if I need to cancel my event?",
    answer:
      "The $250 setup fee is non-refundable. If you need to cancel your event, please contact us as soon as possible to discuss your options. Keep in mind that PayPal fees are non-refundable and will be deducted from your refund.",
  },
  {
    question: "Can I use my own domain and email address?",
    answer:
      "At this time, BadgeReg does not support custom domains or email addresses. You will be issued a domain such as your-event.badgereg.com, and you can point your own custom domain at it as a redirect. Emails are sent from receipts@badgereg.com.",
  },
  {
    question: "Can I export reports about my fundraiser?",
    answer:
      "Yes. From the admin dashboard within your event's domain you can export your participant roster and your counselor list as CSV or Excel files, and review a transaction ledger of every payment, refund, and void.",
  },
  {
    question: "How many classes can we offer?",
    answer:
      "There is no limit on the number of merit badge classes you can offer, and you can run multiple sessions of the same popular class. Each class can have its own capacity limit, age restriction, prerequisites, and price.",
  },
  {
    question: "Can we try BadgeReg before committing?",
    answer:
      "Yes. A live demo of the full product runs at demo.badgereg.com and is linked from the Demo page on this site. It is loaded with sample data, requires no signup, and lets you explore both the parent registration flow and the organizer dashboard.",
  },
];

export default function FAQ() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        flexDirection: "column",
        backgroundColor: "background.default",
      }}
    >
      <Seo
        route="/faqs"
        jsonLd={[faqPageSchema(faqs), breadcrumbSchema("FAQs", "/faqs")]}
      />
      <Container>
        {/* Stacks below 600px: "Frequently Asked Questions" beside the badge wrapped to four lines
            in a narrow column on a phone. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          pt={3}
        >
          <Tooltip title="Digital Technology" arrow placement="top">
            <span>
              <img
                src="badges/DigitalTechnology.svg"
                alt="Digital Technology"
                height={56}
                width={56}
              />
            </span>
          </Tooltip>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: theme.palette.text.primary,
              pb: { xs: 1, sm: 3 },
              pt: 1,
              fontWeight: "bold",
            }}
          >
            Frequently Asked Questions
          </Typography>
        </Stack>
        {faqs.map((faq, index) => (
          <Accordion
            key={index}
            sx={{ mt: 2, backgroundColor: "background.paper" }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${index}a-content`}
              id={`panel${index}a-header`}
            >
              <Typography component="h2" variant="subtitle1">
                {faq.question}
              </Typography>
            </AccordionSummary>
            {/* The summary above has always claimed to control `panel{i}a-content`; nothing carried
                that id, so the association pointed at nothing. Naming the panel here completes it,
                and labelling the panel by its own question is what lets a screen reader user jump
                between answers and still know which question each one belongs to. */}
            <AccordionDetails
              id={`panel${index}a-content`}
              role="region"
              aria-labelledby={`panel${index}a-header`}
            >
              <Typography>{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>
    </Box>
  );
}
